"use client";

import { createContext, useContext } from "react";

/**
 * `adding`: the phone sheet is on its way into the cart.
 * `continuing`: the navigation to the cart has started.
 * Both lock the popup — a decline mid-add would still land the line.
 */
export type PhoneUpsellStatus = "idle" | "adding" | "add_failed" | "continuing";

export function isPhoneUpsellLocked(status: PhoneUpsellStatus): boolean {
  return status === "adding" || status === "continuing";
}

/** The offer as a step another sheet renders, driven by the gate. */
export interface PhoneUpsellStep {
  status: PhoneUpsellStatus;
  /** The offered sheet's ladder price, in öre — from `quoteSheetLadder`. */
  offerPrice: number;
  /** What the ladder takes off it, in öre. `0` hides the struck price and saving. */
  offerSavings: number;
  onAccept: (phoneNumber: string) => void;
  onDecline: () => void;
  /** Closed without choosing: the buyer stays on the page. */
  onDismiss: () => void;
}

export interface PhoneUpsellGateContextValue {
  /**
   * Offer the phone sticker in place of a cart-link navigation to `href`.
   * `true` when the popup took the click over — the caller prevents the
   * default — and `false` when there is nothing to offer and the navigation
   * should go ahead.
   */
  interceptCartNavigation: (href: string) => boolean;
  /**
   * True until the offer for the current cart count has been read. Only the
   * added-to-cart popup waits on it — its "Fortsätt" would otherwise skip
   * the phone step when tapped right after the add.
   */
  isOfferPending: boolean;
  /**
   * The offer as the added-to-cart popup's second step, when a click from
   * that popup was taken over; `null` otherwise.
   */
  addedToCartStep: PhoneUpsellStep | null;
}

/** Provided by `PhoneUpsellGateProvider`; apart from it so cart links stay light. */
export const PhoneUpsellGateContext = createContext<PhoneUpsellGateContextValue | null>(null);

const PASS_THROUGH: PhoneUpsellGateContextValue = {
  interceptCartNavigation: () => false,
  isOfferPending: false,
  addedToCartStep: null,
};

/**
 * The gate, or a pass-through outside `PhoneUpsellGateProvider` (the
 * company shop, tests), where every cart navigation goes straight ahead.
 */
export function usePhoneUpsellGate(): PhoneUpsellGateContextValue {
  return useContext(PhoneUpsellGateContext) ?? PASS_THROUGH;
}
